import { GridColDef } from "@mui/x-data-grid-pro";
import {
  DeleteIcon,
  DownloadIcon,
  GKDataGrid,
  Grid2,
  IconButton,
  InvoiceBillingIcon,
  KebabMenu2Icon,
  NoData,
  PopConfirm,
  Popover,
  Show,
  StatusField,
  Typography,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { useState } from "react";
import {
  useDeleteInvoiceMutation,
  useGetInvoiceListQuery,
} from "store/features/Revenue/ProformaApi";
import { updateFilter } from "store/slices/tableSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { moneyCommaSeparator, numberToCurrency } from "utils/MathFunction";
import { UserRoleTypes } from "utils/common";
import {
  extraColumnsField,
  handleDownloadFile,
  matchDate,
} from "utils/helpers";
import { FinancialYearDropdown, financialYearFilter } from "../utils";

interface Props {
  name?: string;
  tableName?: string;
  portfolioId?: string;
}

const Invoice = (props: Props) => {
  const dispatch = useAppDispatch();

  const { name, tableName, portfolioId } = props;

  const userData = useAppSelector((state) => state?.user?.userData);
  const commonFilter = useAppSelector((state) => state?.table?.commonFilter);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [confirmationModal, setConfirmationModal] =
    useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);
  const popoverId = open ? "simple-popover" : undefined;

  const requestBody = useGenerateQueryParams(name || "invoice", {
    portfolio_id: portfolioId,
  });

  const { data, isFetching } = useGetInvoiceListQuery(requestBody);

  const [deleteInvoice] = useDeleteInvoiceMutation();

  const updateFYFilter = (filterName: string, val: any) => {
    const targetName = filterName || "invoice";
    dispatch(
      updateFilter({
        name: targetName,
        filter: {
          customFilter: {
            financialYear: val?.value,
            start_date: val.dates.startDate,
            end_date: val.dates.endDate,
          },
        },
      }),
    );
  };

  const columns: GridColDef[] = [
    {
      ...extraColumnsField(
        `Invoice No. (${data?.totalCount || 0})`,
        "invoiceNumber",
        150,
        1,
      ),
    },
    {
      ...extraColumnsField("Date", "date", 120, 1),
      valueFormatter: (value) => matchDate(value?.value),
    },
    {
      ...extraColumnsField("Portfolio", "clientName", 200, 1),
      renderCell: (params) => {
        return (
          <Grid2
            container
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Typography fontWeight={700}>{params?.row?.clientName}</Typography>
            <Typography fontSize={12}>
              {params?.row?.portfolioName} - {params?.row?.portfolioId}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField("Taxable Amount (₹)", "taxableFee", 170, 1),
      valueGetter: (row) => moneyCommaSeparator(row?.row?.taxableFee),
      type: "number",
    },
    {
      ...extraColumnsField("Final Amount (₹)", "totalFee", 150, 1),
      valueGetter: (row) => moneyCommaSeparator(row?.row?.totalFee),
      type: "number",
    },
    {
      ...extraColumnsField("Fee Plan Name", "feePlanName", 150, 1),
    },
    {
      ...extraColumnsField("Proforma No.", "proformaNumber", 150, 1),
    },
    {
      ...extraColumnsField("Payment Status", "paymentStatus", 150, 1),
      renderCell: (row) => (
        <StatusField statusString={row?.row?.paymentStatus?.toString()} />
      ),
    },
    {
      ...extraColumnsField("Actions", "action", 150, 1),
      align: "right",
      headerAlign: "right",
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            setInvoiceDetails(params?.row);
            setAnchorEl(event.currentTarget);
          }}
        >
          <KebabMenu2Icon />
        </IconButton>
      ),
    },
  ];

  const deleteDetails = {
    "Client Name": invoiceDetails?.clientName,
    "Invoice No:": invoiceDetails?.invoiceNumber,
    Amount: numberToCurrency(invoiceDetails?.totalFee),
    "Creation Date:": matchDate(invoiceDetails?.date),
  };

  return (
    <>
      <GKDataGrid
        loading={isFetching}
        columns={columns}
        rows={data?.data || []}
        maxHeight={"calc(100vh - 325px)"}
        totalCount={data?.totalCount || 0}
        name={name || "invoice"}
        tableName={tableName || "invoice_list"}
        headerComponent={
          <FinancialYearDropdown
            updateFilter={updateFYFilter}
            tableKey={name || "invoice"}
            selectedValue={
              financialYearFilter?.find(
                (dataItem) =>
                  dataItem.value ===
                  (name
                    ? commonFilter?.[name]?.customFilter?.financialYear
                    : commonFilter?.invoice?.customFilter?.financialYear),
              )?.name
            }
          />
        }
        emptyComponent={
          <NoData
            text="Invoice List is Empty"
            subText="Create and manage client invoices here. Start by converting a proforma or generating a new invoice."
            customImg={
              <InvoiceBillingIcon
                height={80}
                width={80}
                color="var(--neutral-100)"
              />
            }
          />
        }
      />

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {
              width: "158px",
              borderRadius: "8px",
              p: "8px 0",
              border: "1px solid var(--neutral-100)",
              bgcolor: "var(--white)",
              boxShadow: "4px 4px 12px 0px rgba(0, 0, 0, 0.25);",
            },
          },
        }}
      >
        <Grid2 container>
          <Grid2
            size={12}
            p={"12px"}
            display={"flex"}
            alignItems={"center"}
            gap={1}
            sx={{ cursor: "pointer" }}
          >
            <DownloadIcon />
            <Typography
              fontSize={12}
              fontWeight={600}
              color="var(--grey-900)"
              onClick={() => {
                handleDownloadFile({
                  link: invoiceDetails?.invoicePdf,
                  name: invoiceDetails?.invoiceNumber,
                });
                setAnchorEl(null);
              }}
            >
              Download Invoice
            </Typography>
          </Grid2>
          <Show.When isTrue={userData?.userRole === UserRoleTypes.advisor}>
            <Grid2
              size={12}
              p={"12px"}
              display={"flex"}
              alignItems={"center"}
              gap={1}
              color={"var(--warning-700)"}
              sx={{ cursor: "pointer" }}
              onClick={(event) => {
                setConfirmationModal(event.currentTarget);
                setAnchorEl(null);
              }}
            >
              <DeleteIcon />
              <Typography fontSize={12} fontWeight={600}>
                Delete
              </Typography>
            </Grid2>
          </Show.When>
        </Grid2>
      </Popover>

      <Show.When isTrue={Boolean(confirmationModal)}>
        <PopConfirm
          anchorEl={null}
          open={Boolean(confirmationModal)}
          setOpen={setConfirmationModal}
          modalTitle="Delete Invoice?"
          message="You're about to delete this invoice. This action is permanent and cannot be undone."
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          onSuccess={async () => {
            try {
              await deleteInvoice(invoiceDetails.id).unwrap();
            } catch (err) {
              console.error("Delete invoice failed:", err);
            }
          }}
          extraComponent={
            <Grid2 container spacing={1}>
              {Object.entries(deleteDetails).map(([label, value], index) => (
                <Grid2 key={index} container size={12} display="flex">
                  <Grid2 size={5}>
                    <Typography
                      fontSize={12}
                      fontWeight={500}
                      color="var(--neutral-500)"
                    >
                      {label}
                    </Typography>
                  </Grid2>
                  <Grid2 size={7}>
                    <Typography
                      fontSize={12}
                      fontWeight={500}
                      color="var(--neutral-700)"
                    >
                      {value || "-"}
                    </Typography>
                  </Grid2>
                </Grid2>
              ))}
            </Grid2>
          }
        />
      </Show.When>
    </>
  );
};

export default Invoice;
